import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { User, UserRole } from '@/types'
import { ROLE_LABELS } from '@/utils/role-access'
import { Pencil, SignIn, Trash } from 'phosphor-react'

interface UsersColumnDefsParams {
  handleEdit: (user: User) => void
  handleDeleteClick: (id: string) => void
  handleLoginAs?: (user: User) => void
  currentUserId: string | null
  canEdit: boolean
  showLoginAs?: boolean
  hideRoleColumn?: boolean
  showWholesalerColumn?: boolean
  showActorColumns?: boolean
  extraColumns?: ColDef[]
}

const getRoleLabel = (role: UserRole): string => {
  return ROLE_LABELS[role] ?? role
}

export const getUsersColumnDefs = ({
  handleEdit,
  handleDeleteClick,
  handleLoginAs,
  currentUserId,
  canEdit,
  showLoginAs = false,
  hideRoleColumn = false,
  showWholesalerColumn = false,
  showActorColumns = false,
  extraColumns = [],
}: UsersColumnDefsParams): ColDef[] => {
  const columns: ColDef[] = [
    ...(showActorColumns
      ? [
          {
            field: 'companyName',
            headerName: 'Nom commercial',
            flex: 1,
            minWidth: 160,
            valueFormatter: (params: { value?: string }) => params.value || '—',
          } as ColDef,
        ]
      : []),
    {
      field: 'firstname',
      headerName: 'Prénom',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'lastname',
      headerName: 'Nom',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'phone',
      headerName: 'Téléphone',
      flex: 1,
      minWidth: 120,
    },
    ...(showActorColumns
      ? [
          {
            field: 'fluxId',
            headerName: 'Identifiant Flux',
            width: 150,
            minWidth: 150,
            valueFormatter: (params: { value?: number }) =>
              params.value != null ? String(params.value) : '—',
            filter: 'agNumberColumnFilter',
          } as ColDef,
          {
            field: 'siret',
            headerName: 'SIRET',
            width: 160,
            minWidth: 160,
            valueFormatter: (params: { value?: string }) =>
              params.value ? String(params.value) : '—',
            filter: 'agTextColumnFilter',
          } as ColDef,
        ]
      : []),
    ...(showWholesalerColumn
      ? [
          {
            field: 'wholesaler',
            headerName: 'Grossiste',
            flex: 1,
            minWidth: 160,
            valueGetter: (params: ICellRendererParams) => {
              const wholesaler = params.data?.wholesaler
              if (!wholesaler || typeof wholesaler === 'string') return '—'
              const name = `${wholesaler.firstname ?? ''} ${wholesaler.lastname ?? ''}`.trim()
              return name || wholesaler.email || '—'
            },
            filter: 'agTextColumnFilter',
          } as ColDef,
        ]
      : []),
    ...(!hideRoleColumn
      ? [
          {
            field: 'role',
            headerName: 'Rôle',
            width: 150,
            minWidth: 150,
            cellRenderer: (params: ICellRendererParams) => (
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                  params.value === UserRole.ADMIN
                    ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                    : 'bg-[var(--color-info)]/10 text-[var(--color-info)]'
                }`}
              >
                {getRoleLabel(params.value)}
              </span>
            ),
            filter: 'agTextColumnFilter',
            filterValueGetter: (params: ICellRendererParams) => {
              if (!params.data?.role) return ''
              return getRoleLabel(params.data.role)
            },
            filterParams: {
              filterOptions: ['equals'],
            },
          } as ColDef,
        ]
      : []),
    {
      field: 'lastLogin',
      headerName: 'Dernière connexion',
      flex: 1,
      minWidth: 180,
      filter: 'agDateColumnFilter',
      valueFormatter: params => {
        if (!params.value) return 'Jamais'
        return new Date(params.value).toLocaleString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      },
    },
    {
      field: 'isArchived',
      headerName: 'Archivé',
      width: 100,
      cellRenderer: (params: ICellRendererParams) => (
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            params.value
              ? 'bg-[var(--color-error)]/10 text-[var(--color-error)]'
              : 'bg-[var(--color-gray-200)]/10 text-[var(--color-text-muted)]'
          }`}
        >
          {params.value ? 'Oui' : 'Non'}
        </span>
      ),
      filter: 'agTextColumnFilter',
      filterValueGetter: params => {
        if (params.data?.isArchived === undefined || params.data?.isArchived === null) return ''
        return params.data.isArchived ? 'Oui' : 'Non'
      },
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      field: 'createdAt',
      headerName: 'Date de création',
      flex: 1,
      minWidth: 180,
      filter: 'agDateColumnFilter',
      valueFormatter: params => {
        if (!params.value) return ''
        return new Date(params.value).toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Dernière modification',
      flex: 1,
      minWidth: 180,
      filter: 'agDateColumnFilter',
      valueFormatter: params => {
        if (!params.value) return ''
        return new Date(params.value).toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      },
    },
  ]

  if (extraColumns.length > 0) {
    columns.push(...extraColumns)
  }

  const showActions = canEdit || showLoginAs

  const actionsColumn: ColDef = {
    headerName: 'Actions',
    width: showLoginAs ? 190 : 150,
    cellRenderer: (params: ICellRendererParams) => {
      const isCurrentUser = currentUserId && params.data._id === currentUserId

      return (
        <div className="flex items-center gap-2 h-full">
          {showLoginAs && handleLoginAs && !isCurrentUser && (
            <button
              onClick={() => handleLoginAs(params.data)}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-[var(--color-info)] hover:bg-[var(--color-info)]/10 transition-all duration-200"
              title="Se connecter en tant que"
            >
              <SignIn className="w-4 h-4" weight="regular" />
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => handleEdit(params.data)}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-all duration-200"
              title="Modifier"
            >
              <Pencil className="w-4 h-4" weight="regular" />
            </button>
          )}
          {canEdit && !isCurrentUser && (
            <button
              onClick={() => handleDeleteClick(params.data._id)}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-[var(--color-error)] hover:bg-[var(--color-error)]/10 transition-all duration-200"
              title="Supprimer"
            >
              <Trash className="w-4 h-4" weight="regular" />
            </button>
          )}
        </div>
      )
    },
    pinned: 'right',
  }

  if (showActions) {
    columns.push(actionsColumn)
  }

  return columns
}
